import { createApi } from "@reduxjs/toolkit/query/react";
import { ApiEndpoints, baseQueryWithAuth } from "../../../core";
import {
  ApiResponse,
  ICategory,
  CreateCategoryRequest,
  UpdateCategoryRequest,
} from "../../../types";

type GetCategoriesArg =
  | void
  | string
  | {
      type?: string;
      page?: number;
      limit?: number;
    };

export const categoryApi = createApi({
  reducerPath: "categoryApi",
  baseQuery: baseQueryWithAuth,
  tagTypes: ["Categories"],

  endpoints: (builder) => ({
    getCategories: builder.query<ICategory[], GetCategoriesArg>({
      query: (arg) => {
        let type: string | undefined;
        let page: number | undefined;
        let limit: number | undefined;

        if (typeof arg === "string") {
          type = arg;
        } else if (typeof arg === "object" && arg !== null) {
          type = arg.type;
          page = arg.page;
          limit = arg.limit;
        }

        const baseUrl = type
          ? `${ApiEndpoints.getAllCategories}/${type}`
          : ApiEndpoints.category;

        const params: Record<string, string | number> = {};
        if (page !== undefined) params.page = page;
        if (limit !== undefined) {
          params.limit = limit;
        } else {
          // Backwards-compatible default: fetch all when no limit provided
          params.limit = -1;
        }

        return {
          url: baseUrl,
          method: "GET",
          params,
        };
      },
      transformResponse: (res: ApiResponse<ICategory[]>) => {
        return res?.result ?? res?.results ?? [];
      },
      providesTags: ["Categories"],
    }),

    getCategoryById: builder.query<ApiResponse<ICategory>, string>({
      query: (id) => ({
        url: `${ApiEndpoints.category}/${id}`,
        method: "GET",
      }),
      providesTags: (_result, _error, id) => [{ type: "Categories", id }],
    }),

    createCategory: builder.mutation<
      ApiResponse<ICategory>,
      CreateCategoryRequest
    >({
      query: (data) => ({
        url: ApiEndpoints.createCategory,
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["Categories"],
    }),

    editCategory: builder.mutation<
      ApiResponse<ICategory>,
      UpdateCategoryRequest
    >({
      query: ({ id, data }) => ({
        url: `${ApiEndpoints.editCategory}/${id}`,
        method: "PATCH",
        body: data,
      }),
      invalidatesTags: (_result, _error, { id }) => [
        "Categories",
        { type: "Categories", id },
      ],
    }),

    deleteCategory: builder.mutation<ApiResponse<void>, string>({
      query: (id) => ({
        url: `${ApiEndpoints.deleteCategory}/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Categories"],
    }),
  }),
});

export const {
  useGetCategoriesQuery,
  useGetCategoryByIdQuery,
  useCreateCategoryMutation,
  useEditCategoryMutation,
  useDeleteCategoryMutation,
} = categoryApi;
